import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'
import uniqueId from 'lodash/uniqueId'
import { v4 as uuid4 } from 'uuid'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import {
  IActionRepeaterValue,
  IActionType,
  VariableActionOption,
  VariableResultIcon,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { Icon16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIconPicker, MaterialIconPickerBody, Modal } from '@makyoapp/makyo-ui'

import { VariableSimpleActionBody, VariableSimpleActionButton } from './VariableSimpleAction'
import { VariableWrapper } from './VariableWrapper'

export interface VariableIconProps {
  id?: string
  onChange: (value: VariableResultIcon) => void
  actionType: VariableActionOption
  onModalOpen?: () => void
  onModalClose?: () => void
}

export default function VariableIcon({
  id = uniqueId('vi-'),
  onChange,
  actionType = VariableActionOption.None,
  onModalClose,
  onModalOpen,
}: VariableIconProps) {
  const [show, setShow] = useBoolean(false)
  /**
   * prevent dropdown closing when action modal is showing
   */
  const [preventClose, setPreventClose] = useBoolean(false)
  const [value, setValue] = useState<VariableResultIcon>({
    id: uuid4(),
    flag: VariableSelectFeatureFlag.Icon,
    value: 'home',
    actions: undefined,
  })

  const [step, setStep] = useState(1)
  const handleDone = useCallback(
    (value: VariableResultIcon) => {
      onChange(value)
    },
    [onChange]
  )

  return (
    <VariableWrapper
      id={id}
      title={<Trans>Icon</Trans>}
      show={show}
      icon={<Icon16 />}
      onToggle={(nextShow) => {
        !preventClose && setShow(nextShow)
      }}
      defaultHeight={false}
      defaultWidth={false}
      defaultMenuPad={false}
      menuClassName={clsx(step === 1 ? 'w-[280px]' : 'mk-ag-sdd')}
    >
      <VariableIconBody
        value={value}
        step={step}
        setStep={setStep}
        setShow={setShow}
        setPreventClose={setPreventClose}
        onChange={setValue}
        actionType={actionType}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
        onDone={handleDone}
      />
    </VariableWrapper>
  )
}

export function VariableIconBody({
  value,
  step,
  setStep,
  id,
  setShow,
  setPreventClose,
  actionType,
  onChange,
  onModalClose,
  onModalOpen,
  onDone,
}: {
  value: VariableResultIcon
  step: number
  setStep: (next: number) => void
  id?: string
  onChange: (value: VariableResultIcon) => void
  actionType: VariableActionOption
  onModalOpen?: () => void
  onModalClose?: () => void
  setShow: (next: boolean) => void
  setPreventClose?: (next: boolean) => void
  onDone: (value: VariableResultIcon) => void
}) {
  const { t } = useTranslation()

  const onActionDone = useCallback(() => {
    const nextValue = {
      id: value.id,
      flag: VariableSelectFeatureFlag.Icon,
      value: value.value,
      actions: value.actions,
    } as VariableResultIcon
    onChange(nextValue)
    onDone(nextValue)
    setStep(1)
    setShow(false)
  }, [value, setStep, onChange, setShow, onDone])

  return (
    <>
      {step === 1 && (
        <div className={clsx()}>
          <MaterialIconPickerBody
            value={value.value}
            doneLabel={actionType ? t('Select & Continue') : t('Save')}
            wrapperClassName={clsx(actionType === VariableActionOption.Simple && 'min-h-[240px]!')}
            withClose={false}
            settings={{
              fill: 0,
              grade: 0,
              wght: 400,
              opsz: 24,
            }}
            clickAsDone
            searchMs={300}
            searchFilter={async (keyword) => {
              const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
              return aiSearch
            }}
            onClose={() => {
              setShow(false)
            }}
            onClear={() => {
              const newVal: VariableResultIcon = {
                id: value.id,
                flag: VariableSelectFeatureFlag.Icon,
                value: '',
                actions: value.actions,
              }
              onChange(newVal)
              if (actionType === VariableActionOption.None) {
                onDone(newVal)
              }
              setShow(false)
            }}
            clearLabel={t('Clear')}
            onDone={(icon) => {
              const newVal: VariableResultIcon = {
                id: value.id,
                flag: VariableSelectFeatureFlag.Icon,
                value: icon,
                actions: value.actions,
              }
              onChange(newVal)
              if (actionType === VariableActionOption.None) {
                onDone(newVal)
              }
            }}
          />
        </div>
      )}

      {step === 1 && actionType === VariableActionOption.Advanced && <div>Not Available Yet!</div>}
      {/*step === 2 && action === VariableActionOption.Advanced && (
        <div className='p-2 flex flex-col gap-2'>
          <ActionRepeater
            portal
            noWrap
            id={`${id}-actions`}
            values={Array.isArray(actions) ? actions : []}
            onChange={(nextValues) => {
              setActions(nextValues)
            }}
            onModalOpen={() => {
              onModalOpen?.()
              setPreventClose?.(true)
            }}
            onModalClose={() => {
              onModalClose?.()
              setPreventClose?.(false)
            }}
          />
          <Button
            onClick={() => {
              onChange({
                flag: VariableSelectFeatureFlag.Icon,
                value: icon,
                actions: actions
              })
              setStep(1)
              setPreventClose?.(false)
              setShow(false)
            }}
          >
            <Trans>Done</Trans>
          </Button>
        </div>
      )*/}
      {actionType === VariableActionOption.Simple && (
        <Modal.Footer>
          <div className='flex w-full flex-col gap-2 py-2'>
            <VariableSimpleActionBody
              id={`${id ?? uniqueId('vsa-')}-action`}
              value={
                typeof value.actions === 'object' ? (value.actions as Omit<IActionRepeaterValue, 'id'>) : undefined
              }
              onChange={(next) => {
                onChange({
                  id: value.id,
                  flag: VariableSelectFeatureFlag.Icon,
                  actions: next?.type === IActionType.None ? undefined : next,
                  value: value.value,
                })
              }}
              actionTypes={[
                IActionType.GoBack,
                IActionType.GoToScreen,
                IActionType.RefreshScreen,
                IActionType.OpenNavDrawer,
                IActionType.Logout,
                IActionType.OpenLink,
                IActionType.OpenMap,
                IActionType.OpenMenu,
              ]}
            />
            <VariableSimpleActionButton
              className='w-full'
              value={
                typeof value.actions === 'object' ? (value.actions as Omit<IActionRepeaterValue, 'id'>) : undefined
              }
              onClick={() => {
                onActionDone()
              }}
            >
              <Trans>Save</Trans>
            </VariableSimpleActionButton>
          </div>
        </Modal.Footer>
      )}
    </>
  )
}
